import { fromBase64, toBase64 } from "./binary";
import type { LocalBackupSource, LocalSnapshot } from "./repository";
import { ARCHIVE_COLLECTIONS, COLLECTIONS, SCHEMA_VERSION } from "./schema";

const FORMAT = "classroom-local-encrypted-backup";
const ITERATIONS = 310_000;

type PortableHeader = {
  format: "rollclassbackup";
  formatVersion: 1;
  createdAt: string;
  kdf: BackupEnvelope["kdf"];
  cipher: BackupEnvelope["cipher"];
};
export type PortableBackup = LocalBackupSource & { createdAt: string; deviceId: string; checksum: string };

type BackupEnvelope = {
  format: typeof FORMAT;
  version: 1;
  exportedAt: string;
  kdf: { name: "PBKDF2"; hash: "SHA-256"; iterations: number; salt: string };
  cipher: { name: "AES-GCM"; iv: string };
  ciphertext: string;
};

async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
  usage: KeyUsage,
): Promise<CryptoKey> {
  const source = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations },
    source,
    { name: "AES-GCM", length: 256 },
    false,
    [usage],
  );
}

export async function encryptSnapshot(snapshot: LocalSnapshot, passphrase: string): Promise<string> {
  if (passphrase.length < 12) throw new Error("備份密碼至少需要 12 個字元。");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, ITERATIONS, "encrypt");
  const plain = new TextEncoder().encode(JSON.stringify(snapshot));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plain));
  const envelope: BackupEnvelope = {
    format: FORMAT,
    version: 1,
    exportedAt: new Date().toISOString(),
    kdf: { name: "PBKDF2", hash: "SHA-256", iterations: ITERATIONS, salt: toBase64(salt) },
    cipher: { name: "AES-GCM", iv: toBase64(iv) },
    ciphertext: toBase64(ciphertext),
  };
  return JSON.stringify(envelope);
}

export async function decryptSnapshot(contents: string, passphrase: string): Promise<LocalSnapshot> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contents);
  } catch {
    throw new Error("備份檔不是有效 JSON。");
  }
  const envelope = parsed as Partial<BackupEnvelope>;
  if ((parsed as Partial<PortableHeader>)?.format === "rollclassbackup") {
    return (await decryptPortableBackup(contents, passphrase)).snapshot;
  }
  if (
    envelope?.format !== FORMAT ||
    envelope.version !== 1 ||
    envelope.kdf?.name !== "PBKDF2" ||
    envelope.kdf.hash !== "SHA-256" ||
    envelope.kdf.iterations !== ITERATIONS ||
    envelope.cipher?.name !== "AES-GCM" ||
    typeof envelope.cipher.iv !== "string" ||
    typeof envelope.kdf.salt !== "string" ||
    typeof envelope.ciphertext !== "string"
  ) {
    throw new Error("備份檔格式或版本不支援。");
  }
  try {
    const key = await deriveKey(passphrase, fromBase64(envelope.kdf.salt), ITERATIONS, "decrypt");
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(envelope.cipher.iv) as BufferSource },
      key,
      fromBase64(envelope.ciphertext) as BufferSource,
    );
    return JSON.parse(new TextDecoder().decode(plain)) as LocalSnapshot;
  } catch {
    throw new Error("備份密碼錯誤，或檔案已損壞。");
  }
}

function authenticatedHeader(header: PortableHeader): Uint8Array {
  // Fixed ordering authenticates the interpreted header independently of JSON key order.
  return new TextEncoder().encode(
    JSON.stringify([
      header.format,
      header.formatVersion,
      header.createdAt,
      header.kdf.name,
      header.kdf.hash,
      header.kdf.iterations,
      header.kdf.salt,
      header.cipher.name,
      header.cipher.iv,
    ]),
  );
}

async function snapshotChecksum(snapshot: LocalSnapshot, sqliteImage: string | undefined): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify({ snapshot, sqliteImage }));
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function assertBackupSource(source: LocalBackupSource): void {
  if (typeof source?.sqliteImage !== "string" || !source.sqliteImage.length) throw new Error("備份缺少 SQLite 影像。");
  if (!Number.isInteger(source?.schemaVersion) || source.schemaVersion < 1 || source.schemaVersion > SCHEMA_VERSION) {
    throw new Error("備份資料庫版本不支援；請使用相容的程式版本。");
  }
  if (source.sqliteIntegrity?.integrityCheck !== "ok" || source.sqliteIntegrity.foreignKeyViolations !== 0) {
    throw new Error("備份的 SQLite 完整性或關聯檢查未通過。");
  }
  if (
    !source.snapshot?.workspace ||
    !Array.isArray(source.snapshot.records) ||
    !Array.isArray(source.snapshot.hiddenTools)
  ) {
    throw new Error("備份的資料清單無效。");
  }
  for (const record of source.snapshot.records) {
    if (!COLLECTIONS.includes(record?.collection)) throw new Error("備份含有不支援的紀錄類別。");
  }
  for (const collection of COLLECTIONS) {
    const count =
      source.recordCounts?.[collection] ??
      ((source.schemaVersion < 9 && (collection === "personalNotes" || collection === "personalNotePages")) ||
      (source.schemaVersion < 4 && ARCHIVE_COLLECTIONS.includes(collection as (typeof ARCHIVE_COLLECTIONS)[number]))
        ? 0
        : undefined);
    if (
      !Number.isInteger(count) ||
      count !== source.snapshot.records.filter((row) => row.collection === collection).length
    ) {
      throw new Error("備份的紀錄數量不一致。");
    }
  }
}

export async function encryptPortableBackup(
  source: LocalBackupSource,
  passphrase: string,
  deviceId: string,
): Promise<string> {
  if (passphrase.length < 12) throw new Error("備份密碼至少需要 12 個字元。");
  if (!deviceId.trim() || deviceId.length > 128) throw new Error("備份裝置代碼無效。");
  assertBackupSource(source);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header: PortableHeader = {
    format: "rollclassbackup",
    formatVersion: 1,
    createdAt: new Date().toISOString(),
    kdf: { name: "PBKDF2", hash: "SHA-256", iterations: ITERATIONS, salt: toBase64(salt) },
    cipher: { name: "AES-GCM", iv: toBase64(iv) },
  };
  const payload: PortableBackup = {
    ...source,
    createdAt: header.createdAt,
    deviceId,
    checksum: await snapshotChecksum(source.snapshot, source.sqliteImage),
  };
  const key = await deriveKey(passphrase, salt, ITERATIONS, "encrypt");
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: authenticatedHeader(header) as BufferSource, tagLength: 128 },
    key,
    new TextEncoder().encode(JSON.stringify(payload)),
  );
  return JSON.stringify({ ...header, ciphertext: toBase64(new Uint8Array(ciphertext)) });
}

export async function decryptPortableBackup(contents: string, passphrase: string): Promise<PortableBackup> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contents);
  } catch {
    throw new Error("備份檔不是有效 JSON。");
  }
  const header = parsed as PortableHeader & { ciphertext: string };
  if (
    header?.format !== "rollclassbackup" ||
    header.formatVersion !== 1 ||
    typeof header.createdAt !== "string" ||
    !Number.isFinite(Date.parse(header.createdAt)) ||
    header.kdf?.name !== "PBKDF2" ||
    header.kdf.hash !== "SHA-256" ||
    header.kdf.iterations !== ITERATIONS ||
    typeof header.kdf.salt !== "string" ||
    header.cipher?.name !== "AES-GCM" ||
    typeof header.cipher.iv !== "string" ||
    typeof header.ciphertext !== "string"
  ) {
    throw new Error("備份檔格式或版本不支援。");
  }
  let payload: PortableBackup;
  try {
    const salt = fromBase64(header.kdf.salt);
    const iv = fromBase64(header.cipher.iv);
    if (salt.length !== 16 || iv.length !== 12) throw new Error("invalid salt or IV");
    const key = await deriveKey(passphrase, salt, ITERATIONS, "decrypt");
    const plain = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv as BufferSource,
        additionalData: authenticatedHeader(header) as BufferSource,
        tagLength: 128,
      },
      key,
      fromBase64(header.ciphertext) as BufferSource,
    );
    payload = JSON.parse(new TextDecoder().decode(plain)) as PortableBackup;
  } catch {
    throw new Error("備份密碼錯誤，或檔案已損壞。");
  }
  assertBackupSource(payload);
  if (
    payload.createdAt !== header.createdAt ||
    typeof payload.deviceId !== "string" ||
    !payload.deviceId.trim() ||
    payload.deviceId.length > 128 ||
    payload.checksum !== (await snapshotChecksum(payload.snapshot, payload.sqliteImage))
  ) {
    throw new Error("備份的完整性檢查失敗，檔案已損壞。");
  }
  return payload;
}

export async function decryptBackupFile(
  contents: string,
  passphrase: string,
): Promise<{ snapshot: LocalSnapshot; sqliteImage?: string; schemaVersion?: number }> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contents);
  } catch {
    throw new Error("備份檔不是有效 JSON。");
  }
  if ((parsed as Partial<PortableHeader>)?.format === "rollclassbackup")
    return decryptPortableBackup(contents, passphrase);
  return { snapshot: await decryptSnapshot(contents, passphrase) };
}
